import { IntlMessageFormat } from 'intl-messageformat';
import {
	isArgumentElement,
	isPluralElement,
	isSelectElement,
	isTagElement,
	type MessageFormatElement,
} from '@formatjs/icu-messageformat-parser';
import {
	defaultLanguage,
	defaultMessages,
	languages,
	loaders,
} from 'virtual:pwa-i18n';

import { reportClientError } from '@shared/observability';
import { resolvePreferredLanguage } from '@shared/lib/preferred-language';
import { PSEUDO_LANGUAGE, uiLanguageDirection } from './languages';
import type { ArgsOf, MessageKey } from './types';

/**
 * The app language, and the catalogs to label the UI in it.
 *
 * One store outside React, so model code (toasts, error handlers) can
 * `translate()` too; components read it through `useT()`/`useLanguage()`
 * (`./react.tsx`). The catalogs come from `virtual:pwa-i18n`
 * (scripts/vite/pwa-i18n-plugin.ts): the tenant's default language inline,
 * each other enabled language as its own chunk, loaded on first use.
 * They are already merged (shared → template → tenant), complete, and
 * pre-parsed, so a lookup can't miss -- a miss here means something got past
 * the build, and is reported rather than thrown.
 */

/** A merged catalog: key → pre-parsed ICU message (a string in tests). */
export type CompiledCatalog = Record<string, string | Array<MessageFormatElement>>;

// Same key the rest of the app has always used (constants.ts LANGUAGE_KEY);
// spelled out here because @shared can't import the app's constants.
const STORAGE_KEY = 'language';

const catalogs = new Map<string, CompiledCatalog>([
	[defaultLanguage, defaultMessages],
]);
const formatters = new Map<string, Formatter>();

/** A message ready to format, and its arguments that print a value as-is. */
interface Formatter {
	format: IntlMessageFormat;
	plainArguments: Set<string>;
}
const listeners = new Set<() => void>();

let current = defaultLanguage;
let pending: string | undefined;

function readSaved(): string | null {
	try {
		return localStorage.getItem(STORAGE_KEY);
	} catch {
		return null;
	}
}

/** The language the reader wants: saved and still offered, else the default. */
export function preferredLanguage(): string {
	const saved = readSaved();
	// The dev-only pseudo-locale isn't a tenant language, but it has a loader.
	if (import.meta.env.DEV && saved === PSEUDO_LANGUAGE && loaders[saved]) {
		return saved;
	}
	return resolvePreferredLanguage(saved, defaultLanguage, languages);
}

async function load(language: string): Promise<CompiledCatalog | undefined> {
	const loaded = catalogs.get(language);
	if (loaded) return loaded;
	const loader = loaders[language];
	if (!loader) return undefined;
	const { default: catalog } = await loader();
	catalogs.set(language, catalog);
	return catalog;
}

function applyToDocument(language: string) {
	const root = document.documentElement;
	root.lang = language;
	root.dir = uiLanguageDirection(language);
}

function emit() {
	for (const listener of listeners) listener();
}

/**
 * Switch the app language: persists the choice, loads its catalog, then
 * switches everything at once (UI labels and content follow `getLanguage()`).
 * A language the tenant doesn't offer is ignored.
 */
export async function setLanguage(language: string): Promise<void> {
	if (!languages.includes(language) && language !== defaultLanguage) {
		if (!(import.meta.env.DEV && loaders[language])) return;
	}
	try {
		localStorage.setItem(STORAGE_KEY, language);
	} catch {
		// Private mode: still applies for the session.
	}
	pending = language;
	try {
		await load(language);
	} catch (error) {
		reportClientError({ source: 'i18n', category: 'load', error });
		return;
	}
	// A later call won the race.
	if (pending !== language) return;
	current = language;
	applyToDocument(language);
	emit();
}

/** Start in the reader's language. Called once, before the first render. */
export function startI18n(): Promise<void> {
	applyToDocument(current);
	const wanted = preferredLanguage();
	return wanted === current ? Promise.resolve() : setLanguage(wanted);
}

export function getLanguage(): string {
	return current;
}

export function subscribe(listener: () => void): () => void {
	listeners.add(listener);
	return () => listeners.delete(listener);
}

/**
 * The arguments a message prints as-is (`{name}`), as opposed to ones that
 * pick a branch or format a number (`{interval, select, ...}`, `{count,
 * plural, ...}`) -- only the former may be bidi-isolated, or a select would
 * stop matching.
 */
function plainArguments(elements: Array<MessageFormatElement>): Set<string> {
	const plain = new Set<string>();
	const other = new Set<string>();
	const walk = (list: Array<MessageFormatElement>) => {
		for (const element of list) {
			if (isArgumentElement(element)) plain.add(element.value);
			else if ('value' in element && typeof element.value === 'string' && element.type !== 0)
				other.add(element.value);
			if (isSelectElement(element) || isPluralElement(element))
				for (const option of Object.values(element.options)) walk(option.value);
			if (isTagElement(element)) walk(element.children);
		}
	};
	walk(elements);
	for (const name of other) plain.delete(name);
	return plain;
}

/**
 * First-strong isolate / pop directional isolate: the text form of `<bdi>`.
 * A value interpolated into a label (an issue's name, the tenant's title) is
 * in its own language, so it keeps its own direction and punctuation --
 * "Remove “Yum!” from downloads" in an Arabic label, not "!Yum". Works in
 * plain strings too (aria-labels, toasts), where an element can't go.
 *
 * Only a value with letters running the other way from the app needs it
 * (Latin in an Arabic app, Hebrew in an English one); numbers and
 * same-direction text are left alone, so labels don't fill with invisible
 * characters.
 */
const FSI = '\u2068';
const PDI = '\u2069';
const RTL_LETTER = /[\u0590-\u08FF\uFB1D-\uFDFF\uFE70-\uFEFF]/;
const LTR_LETTER = /(?![\u0590-\u08FF\uFB1D-\uFDFF\uFE70-\uFEFF])\p{L}/u;

function isolate(
	values: Record<string, unknown> | undefined,
	plain: Set<string>,
	direction: 'ltr' | 'rtl',
) {
	if (!values) return values;
	const opposite = direction === 'rtl' ? LTR_LETTER : RTL_LETTER;
	let isolated: Record<string, unknown> | undefined;
	for (const name of plain) {
		const value = values[name];
		if (typeof value === 'string' && opposite.test(value)) {
			isolated ??= { ...values };
			isolated[name] = `${FSI}${value}${PDI}`;
		}
	}
	return isolated ?? values;
}

function formatterFor(language: string, key: string): Formatter | undefined {
	const cacheKey = `${language}\u0000${key}`;
	const cached = formatters.get(cacheKey);
	if (cached) return cached;
	const message =
		catalogs.get(language)?.[key] ?? catalogs.get(defaultLanguage)?.[key];
	if (message === undefined) return undefined;
	// The pseudo-locale formats numbers and plurals as English.
	const locale = language === 'en-XA' ? 'en' : language;
	const format = new IntlMessageFormat(message, locale);
	const formatter = { format, plainArguments: plainArguments(format.getAst()) };
	formatters.set(cacheKey, formatter);
	return formatter;
}

const reported = new Set<string>();

function reportOnce(key: string, error: unknown) {
	if (reported.has(key)) return;
	reported.add(key);
	reportClientError({ source: 'i18n', category: 'format', error });
}

/**
 * Format a message with any values, including rich-text tag functions
 * (`<T>` passes React ones). A string value printed as-is that runs the other
 * way from the app is bidi-isolated (`isolate`). Never throws: an unknown key or a bad value comes back as the
 * key, reported once.
 */
export function formatMessage(
	key: string,
	values?: Record<string, unknown>,
): string | Array<unknown> {
	const formatter = formatterFor(current, key);
	if (!formatter) {
		reportOnce(key, new Error(`i18n: no message "${key}" for "${current}"`));
		return key;
	}
	try {
		return formatter.format.format(
			isolate(values, formatter.plainArguments, uiLanguageDirection(current)) as never,
		) as string | Array<unknown>;
	} catch (error) {
		reportOnce(key, error);
		return key;
	}
}

type TranslateArgs<K extends MessageKey> = {} extends ArgsOf<K>
	? [values?: ArgsOf<K>]
	: [values: ArgsOf<K>];

/**
 * A label in the current language, for code outside React (toasts, error
 * handlers). Components use `useT()`, which re-renders on a language change.
 */
export function translate<K extends MessageKey>(
	key: K,
	...[values]: TranslateArgs<K>
): string {
	const result = formatMessage(key, values as Record<string, unknown>);
	return Array.isArray(result) ? result.join('') : result;
}

export type { TranslateArgs };
